import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'

export default function UnderwritingBasicsPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-8 p-4 py-12">
      <Link to="/resources" className="text-amber-600 hover:underline">
        &larr; Back to Resources
      </Link>
      <h1 className="text-3xl font-bold">How We Stress-Test Every Deal</h1>

      <Card>
        <CardContent className="p-6">
          <p className="text-lg">
            Underwriting is the process of analyzing whether a property will make money. It's the single most important
            skill in real estate investing. Bad underwriting leads to bad deals. Here's how we evaluate every property
            at Fairmeadow Capital.
          </p>
        </CardContent>
      </Card>

      <div className="space-y-8">
        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">The Five Numbers That Matter</h2>
          <p className="text-muted-foreground">
            Every deal comes down to five core metrics. If these don't work, the deal doesn't work, regardless of how
            good the property looks.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardContent className="p-5">
                <h3 className="mb-2 text-lg font-semibold">Net Operating Income (NOI)</h3>
                <p className="text-muted-foreground mb-2 text-sm">
                  Gross rental income minus operating expenses (taxes, insurance, maintenance, management, vacancy).
                  Does not include debt service.
                </p>
                <code className="bg-muted rounded px-2 py-1 text-sm">
                  NOI = Gross Rent - Vacancy - Operating Expenses
                </code>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <h3 className="mb-2 text-lg font-semibold">Cap Rate</h3>
                <p className="text-muted-foreground mb-2 text-sm">
                  Annual NOI as a percentage of purchase price. Measures the unlevered return. In Cleveland, we target
                  8-12% cap rates on small multifamily.
                </p>
                <code className="bg-muted rounded px-2 py-1 text-sm">Cap Rate = NOI / Purchase Price</code>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <h3 className="mb-2 text-lg font-semibold">Cash-on-Cash Return</h3>
                <p className="text-muted-foreground mb-2 text-sm">
                  Annual cash flow (after debt service) divided by total cash invested. This is your actual return on
                  the dollars you put in. We target 8-12%.
                </p>
                <code className="bg-muted rounded px-2 py-1 text-sm">CoC = Annual Cash Flow / Total Cash Invested</code>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <h3 className="mb-2 text-lg font-semibold">DSCR</h3>
                <p className="text-muted-foreground mb-2 text-sm">
                  Debt Service Coverage Ratio. How many times the property's NOI covers the debt payment. Lenders
                  require 1.20x+. We won't buy below 1.25x.
                </p>
                <code className="bg-muted rounded px-2 py-1 text-sm">DSCR = NOI / Annual Debt Service</code>
              </CardContent>
            </Card>

            <Card className="md:col-span-2">
              <CardContent className="p-5">
                <h3 className="mb-2 text-lg font-semibold">Gross Rent Multiplier (GRM)</h3>
                <p className="text-muted-foreground mb-2 text-sm">
                  Purchase price divided by annual gross rent. A quick screening tool. In Cleveland, we look for GRMs
                  under 6-7x on small multifamily, meaning the property costs less than 7 years of gross rent.
                </p>
                <code className="bg-muted rounded px-2 py-1 text-sm">GRM = Purchase Price / Annual Gross Rent</code>
              </CardContent>
            </Card>
          </div>
        </section>

        <Separator />

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">How We Stress-Test</h2>
          <p className="text-muted-foreground">
            The numbers above only matter if they hold up under pressure. We underwrite every deal with conservative
            assumptions:
          </p>

          <div className="space-y-3">
            {[
              {
                label: 'Vacancy',
                value: '8-10%',
                note: 'Even in a strong rental market. Accounts for turnover, eviction, and make-ready time.',
              },
              {
                label: 'Maintenance',
                value: '10% of gross rent',
                note: 'Older buildings need more. We budget higher for pre-1960 construction.',
              },
              {
                label: 'CapEx Reserve',
                value: '5-8% of gross rent',
                note: 'Roof, HVAC, water heater, plumbing. These are coming whether you budget for them or not.',
              },
              {
                label: 'Management',
                value: '8-10% of collected rent',
                note: 'Even if self-managing today. The property should support professional management.',
              },
              {
                label: 'Interest Rate',
                value: 'Current + 1%',
                note: 'We underwrite at a higher rate than quoted. If rates rise before close, the deal still works.',
              },
            ].map((item) => (
              <Card key={item.label}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="font-semibold">{item.label}</span>
                      <p className="text-muted-foreground mt-1 text-sm">{item.note}</p>
                    </div>
                    <span className="font-semibold whitespace-nowrap text-amber-500">{item.value}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <Separator />

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold">Red Flags in Underwriting</h2>
          <ul className="text-muted-foreground space-y-2">
            <li className="flex gap-2">
              <span className="shrink-0 text-red-400">&#x2717;</span> Seller's pro forma uses 3-5% vacancy in a market
              that averages 8%+
            </li>
            <li className="flex gap-2">
              <span className="shrink-0 text-red-400">&#x2717;</span> No budget for CapEx on a building with a
              20-year-old roof
            </li>
            <li className="flex gap-2">
              <span className="shrink-0 text-red-400">&#x2717;</span> Rents are "projected" at above-market rates with
              no renovation plan
            </li>
            <li className="flex gap-2">
              <span className="shrink-0 text-red-400">&#x2717;</span> The deal only works with optimistic appreciation
              assumptions
            </li>
            <li className="flex gap-2">
              <span className="shrink-0 text-red-400">&#x2717;</span> Operating expenses are suspiciously low (often
              missing management or reserves)
            </li>
          </ul>
        </section>

        <Card>
          <CardContent className="p-6 text-center">
            <p className="mb-2 text-lg font-semibold">Try it yourself</p>
            <p className="text-muted-foreground mb-4">
              Use our calculators to practice underwriting with real numbers.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/resources/calculators/cash-on-cash" className="text-amber-600 hover:underline">
                Cash-on-Cash Calculator &rarr;
              </Link>
              <Link to="/resources/calculators/dscr" className="text-amber-600 hover:underline">
                DSCR Calculator &rarr;
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
